import { useMemo } from "react";
import { useTheme } from "next-themes";
import nickWooleyPhoto from "@/assets/nick-wooley.webp";
import wolvesdenLogo from "@/assets/wolvesden-logo.png";
import jayBoydPhoto from "@/assets/testimonials/jay-boyd-avatar.webp";
import rightQuestionLogoLight from "@/assets/testimonials/right-question-light.png";
import rightQuestionLogoDark from "@/assets/testimonials/right-question-dark.png";

interface Testimonial {
  photo: string;
  name: string;
  title: string;
  quote: string;
  logo: string;
  logoDark?: string;
  logoAlt: string;
}

const testimonials: Testimonial[] = [
  {
    photo: nickWooleyPhoto,
    name: "Nick Woolley",
    title: "Founder @ Wolvesden",
    quote: "Everything is connected, understandable, and justifiable. It doesn't just do the discovery, Auditic puts you in the strongest position possible to win the work and deliver on it.",
    logo: wolvesdenLogo,
    logoAlt: "Wolvesden",
  },
  {
    photo: jayBoydPhoto,
    name: "Jay Boyd",
    title: "Founder @ Right Question",
    quote: "Starting from zero, I didn't want to invent a process from scratch. I chose Auditic as the backbone of my audit delivery. The structure is there — discovery to deals — and I'm building on it.",
    logo: rightQuestionLogoLight,
    logoDark: rightQuestionLogoDark,
    logoAlt: "Right Question",
  },
];

export function SignupTestimonialCard() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Pick a random testimonial on mount (stable across re-renders)
  const testimonial = useMemo(
    () => testimonials[Math.floor(Math.random() * testimonials.length)],
    []
  );

  const logoSrc = isDark && testimonial.logoDark ? testimonial.logoDark : testimonial.logo;

  return (
    <div className="relative z-10 w-full max-w-sm">
      <div className="rounded-[22px] bg-card/80 dark:bg-card/40 backdrop-blur-xl border border-border/40 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] text-center space-y-5">
        {/* Quote mark */}
        <div className="flex justify-start">
          <svg className="h-8 w-8 text-primary/40" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
        </div>

        {/* Avatar */}
        <div className="flex justify-center">
          <img
            src={testimonial.photo}
            alt={testimonial.name}
            className="h-20 w-20 rounded-full object-cover border-[3px] border-primary/20 shadow-md"
          />
        </div>

        {/* Name & Title */}
        <div>
          <p className="text-lg font-bold text-foreground">{testimonial.name}</p>
          <p className="text-sm text-muted-foreground">{testimonial.title}</p>
        </div>

        {/* Quote */}
        <blockquote className="text-[15px] italic text-foreground/80 leading-relaxed px-2">
          "{testimonial.quote}"
        </blockquote>

        {/* Stars */}
        <div className="flex justify-center gap-1">
          {[...Array(5)].map((_, i) => (
            <svg key={i} className="h-5 w-5 fill-[hsl(45,100%,51%)] text-[hsl(45,100%,51%)]" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          ))}
        </div>

        {/* Logo */}
        <div className="pt-2 border-t border-border/30 flex justify-center">
          <img
            src={logoSrc}
            alt={testimonial.logoAlt}
            className="h-6 mt-3 opacity-70"
          />
        </div>
      </div>
    </div>
  );
}
